<template>
  <el-dialog
    v-model="isVisible"
    :title="header"
    width="90%"
    @opened="setCurrent"
    @close="close"
  >
    <el-form ref="formObj" v-loading="withLoader" :model="form" :rules="rules">
      <el-form-item
        v-show="rules.name"
        label="Title"
        prop="name"
        :error="errors.name"
      >
        <el-input v-model="form.name" autocomplete="off" />
      </el-form-item>
      <el-form-item
        v-if="rules.name"
        label="Stream"
        prop="stream"
        :error="errors.stream"
      >
        <StreamSelect
          v-model="form.stream"
          :disabled="editDisabled"
          :with-empty="false"
          :radio-view="true"
        />
      </el-form-item>
      <el-form-item
        v-if="rules.name"
        label="Format"
        prop="type_id"
        :error="errors.type_id"
      >
        <AdTypeSelect
          v-model="form.type_id"
          :radio-view="true"
          :disabled="editDisabled"
        />
      </el-form-item>
      <el-form-item
        v-show="spotIdsVisible"
        :label="ipsLabel"
        prop="spot_ids"
        :error="errors.spot_ids"
      >
        <SpotsSelector
          v-model="form.spot_ids"
          :stream="form.stream"
          :type-id="form.type_id"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="close">Cancel</el-button>
        <el-button type="primary" @click="save">{{ saveBtnText }}</el-button>
      </span>
    </template>
  </el-dialog>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed, onMounted } from 'vue'
import useResize from '@/hooks/useResize'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import { CampaignValidationErrorsKey, AdType, Stream } from '@/api/types'
import useValidation from '@/hooks/useValidation'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'

const SAVE_MODE = 'save'
const ADD_MODE = 'add'
const REMOVE_MODE = 'remove'
const CLONE_MODE = 'clone'

export default defineComponent({
  name: 'Modal',
  components: {
    SpotsSelector,
    AdTypeSelect,
    StreamSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      required: true
    }
  },
  setup(props, context) {
    const spotsListsStore = useSpotsListsStore()
    const isVisible = ref(props.visible)
    const withLoader = ref(props.loading)
    const mode = ref(props.mode)

    const formObj = ref(null)
    const rule = {
      required: true,
      message: 'This field is required',
      trigger: 'change'
    }

    const rules = computed(() => {
      const reulstRules: Record<string, any> = { spot_ids: rule }

      if (selected.value.length < 1 || mode.value === SAVE_MODE) {
        reulstRules['name'] = rule
      } else {
        delete reulstRules['name']
      }
      return reulstRules
    })

    const form = ref({
      name: '',
      spot_ids: [],
      type_id: AdType.Popunder,
      stream: Stream.Adult
    } as Record<string, any>)

    const selected = computed(() => {
      return selectedArray.value
        ? selectedArray.value.map(({ attributes }) => attributes)
        : []
    })

    const header = computed(() => {
      return selected.value.length && mode.value !== SAVE_MODE
        ? 'Choosen lists:' +
            selected.value
              .map(({ id, name }) => {
                return `${id} ${name}`
              })
              .join(', ')
        : 'Spots list'
    })

    const ipsLabel = computed(() => {
      let label = 'Spots'
      if (selected.value.length) {
        label = `${label} to ${mode.value === REMOVE_MODE ? 'Remove' : 'Add'}`
      }
      return label
    })

    const save = () => {
      // @ts-ignore: Object is possibly 'null'.
      formObj.value.validate(async (valid: boolean) => {
        if (valid) {
          context.emit('save', form.value)
        }
      })
    }

    const { validationErrors } = useValidation(CampaignValidationErrorsKey)
    const errors = validationErrors()

    const close = () => {
      // @ts-ignore: Object is possibly 'null'.
      formObj.value.resetFields()
      context.emit('close')
    }

    const saveBtnText = computed(() => {
      return selected.value.length
        ? `${mode.value === REMOVE_MODE ? 'Remove' : 'Add'}`
        : 'Save'
    })

    const disabled = computed(() => {
      return form.value && form.value.type_id && form.value.stream
    })

    const editDisabled = computed(() => {
      return (
        mode.value === CLONE_MODE ||
        (mode.value === SAVE_MODE && form.value && !!form.value.id)
      )
    })

    const spotIdsVisible = computed(() => {
      return (
        [REMOVE_MODE, ADD_MODE].includes(mode.value) ||
        !!(form.value && form.value.type_id && form.value.stream)
      )
    })

    const selectedArray = computed(() => {
      return Object.values(spotsListsStore.selected)
    })

    const setCurrent = () => {
      if (spotsListsStore.current) {
        form.value = spotsListsStore.current
      } else if (
        selectedArray.value &&
        [REMOVE_MODE, ADD_MODE].includes(mode.value)
      ) {
        form.value = {
          name: '',
          spot_ids: [],
          type_id: selectedArray.value[0].attributes.type_id,
          stream: selectedArray.value[0].attributes.stream
        }
      } else {
        form.value = {
          name: '',
          spot_ids: [],
          type_id: AdType.Popunder,
          stream: Stream.Adult
        }
      }
      // @ts-ignore: Object is possibly 'null'.
      formObj.value.resetFields()
    }

    watch(
      (_: any) => props.visible,
      (val: any) => {
        isVisible.value = val
      }
    )
    watch(
      (_: any) => props.loading,
      (val: any) => {
        withLoader.value = val
      }
    )
    watch(
      (_: any) => props.mode,
      (val: any) => {
        mode.value = val
      }
    )

    return {
      isVisible,
      form,
      setCurrent,
      header,
      rules,
      save,
      close,
      formObj,
      errors,
      withLoader,
      saveBtnText,
      ipsLabel,
      disabled,
      editDisabled,
      spotIdsVisible
    }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
</style>
